// Main menu screen - hub for all game activities

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';
import { GameState, getCurrentTeam, getTeamsInDivision, getCurrentMonthType } from '../systems/GameState';
import { GAME_CONSTANTS, MONTH_TYPE } from '../data/constants';
import { isHealthy } from '../entities/Gladiator';

export class MainMenu implements Screen {
  readonly state = ScreenState.MAIN_MENU;
  private context: ScreenContext;
  private gameState: GameState | null = null;
  private selectedGladiatorIndex: number = 0;
  private onStartSpectatorBattle: (() => void) | null = null;

  constructor(context: ScreenContext, onStartSpectatorBattle?: () => void) {
    this.context = context;
    this.onStartSpectatorBattle = onStartSpectatorBattle ?? null;
  }

  setGameState(gameState: GameState): void {
    this.gameState = gameState;
  }

  enter(): void {
    console.log('Entering MainMenu');
    this.selectedGladiatorIndex = 0;
  }

  exit(): void {
    console.log('Exiting MainMenu');
  }

  update(_deltaTime: number): void {
    // No animation needed
  }

  render(canvas: Canvas): void {
    if (!this.gameState) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      canvas.drawText('Loading...', 400, 260, CONSTANTS.BIG_FONT, '#000000', 'center');
      return;
    }

    const team = getCurrentTeam(this.gameState);
    if (!team) {
      canvas.clear(CONSTANTS.MAIN_BACKGROUND);
      canvas.drawText('No team selected', 400, 260, CONSTANTS.BIG_FONT, '#000000', 'center');
      return;
    }

    // Background
    canvas.clear(CONSTANTS.MAIN_BACKGROUND);

    // Top bar - team info
    this.renderTopBar(canvas, team);

    // Left side - gladiator list
    this.renderGladiatorList(canvas, team);

    // Center - selected gladiator details
    this.renderGladiatorDetails(canvas, team);

    // Right side - division standings
    this.renderDivisionStandings(canvas, team);

    // Bottom - action buttons
    this.renderActionButtons(canvas, team);
  }

  private renderTopBar(canvas: Canvas, team: any): void {
    // Top bar background
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, 50, CONSTANTS.TOP1_BACKGROUND);
    canvas.drawRect(0, 50, CONSTANTS.CANVAS_WIDTH, 30, CONSTANTS.TOP2_BACKGROUND);

    // Team name
    canvas.drawText(team.name, 20, 12, CONSTANTS.BIG_FONT, '#000000');

    // Month and year
    const monthName = GAME_CONSTANTS.MONTHS[this.gameState!.month];
    const yearText = `${this.gameState!.year} ${GAME_CONSTANTS.YEAR_SUFFIX}`;
    canvas.drawText(`${monthName} ${yearText}`, 400, 12, CONSTANTS.BIG_FONT, '#000000', 'center');

    // Money
    const moneyText = `${GAME_CONSTANTS.MONEY_PREFIX}: ${team.money} ${GAME_CONSTANTS.MONEY_SUFFIX}`;
    canvas.drawText(moneyText, CONSTANTS.CANVAS_WIDTH - 20, 12, CONSTANTS.BIG_FONT, '#000000', 'right');

    // Second row - ratings and month type
    canvas.drawText(`Arvostelu: ${team.rating}`, 20, 55, CONSTANTS.MAIN_FONT, '#000000');
    canvas.drawText(`Areenapisteet: ${team.arenaPoints}`, 200, 55, CONSTANTS.MAIN_FONT, '#000000');

    // Month type indicator
    const monthType = getCurrentMonthType(this.gameState!);
    let monthTypeText = '';
    if (monthType === MONTH_TYPE.DIVISION_BATTLE) {
      monthTypeText = 'Sarjaottelu';
    } else if (monthType === MONTH_TYPE.CUP_BATTLE) {
      monthTypeText = 'Cup-ottelu';
    } else {
      monthTypeText = 'Talvitauko';
    }
    canvas.drawText(monthTypeText, 400, 55, CONSTANTS.MAIN_FONT, '#000000', 'center');

    // Race of month
    const raceText = `Kuukauden rotu: ${this.gameState!.raceOfMonth.name}`;
    canvas.drawText(raceText, CONSTANTS.CANVAS_WIDTH - 20, 55, CONSTANTS.MAIN_FONT, CONSTANTS.RACE_OF_MONTH, 'right');
  }

  private renderGladiatorList(canvas: Canvas, team: any): void {
    const listX = 20;
    const listY = 95;
    const listWidth = 200;
    const listHeight = 330;

    // List background
    canvas.drawRect(listX, listY, listWidth, listHeight, '#ffffff');
    canvas.drawRectOutline(listX, listY, listWidth, listHeight, CONSTANTS.TABLE_BORDER);

    // Header
    canvas.drawText('Gladiaattorit', listX + 10, listY + 5, CONSTANTS.MAIN_FONT, '#000000');

    // Gladiator entries
    const gladiatorIds = team.gladiatorIds || [];
    const itemHeight = 36;
    const startY = listY + 25;

    for (let i = 0; i < gladiatorIds.length; i++) {
      const gladiator = this.gameState!.gladiators.get(gladiatorIds[i]);
      if (!gladiator) continue;

      const itemY = startY + i * itemHeight;

      // Selection highlight
      if (i === this.selectedGladiatorIndex) {
        canvas.drawRect(listX + 2, itemY, listWidth - 4, itemHeight - 2, CONSTANTS.SELECTED_GLADIATOR);
      }

      // Portrait
      const portrait = this.context.assets.gladiators[gladiator.race.id];
      if (portrait) {
        canvas.drawImage(portrait, listX + 5, itemY + 2, 32, 32);
      }

      // Name and race
      const nameColor = gladiator.isSpecial ? CONSTANTS.SPECIAL_GLADIATOR_TITLE : CONSTANTS.GLADIATOR_TITLE;
      canvas.drawText(gladiator.name, listX + 42, itemY + 3, CONSTANTS.MAIN_FONT, nameColor);
      canvas.drawText(gladiator.race.name, listX + 42, itemY + 17, CONSTANTS.MSG_FONT, '#666666');

      // Health indicator
      if (!isHealthy(gladiator)) {
        canvas.drawText(`(${gladiator.hurt}kk)`, listX + listWidth - 35, itemY + 10, CONSTANTS.MSG_FONT, '#cc0000');
      }

      // Click region
      canvas.addClickableRegion({
        id: `gladiator-${i}`,
        x: listX,
        y: itemY,
        width: listWidth,
        height: itemHeight,
        onClick: () => {
          this.selectedGladiatorIndex = i;
          this.gameState!.currentGladiatorId = gladiatorIds[i];
        },
      });
    }
  }

  private renderGladiatorDetails(canvas: Canvas, team: any): void {
    const detailX = 230;
    const detailY = 95;
    const detailWidth = 330;
    const detailHeight = 330;

    // Background
    canvas.drawRect(detailX, detailY, detailWidth, detailHeight, '#ffffff');
    canvas.drawRectOutline(detailX, detailY, detailWidth, detailHeight, CONSTANTS.TABLE_BORDER);

    const gladiatorIds = team.gladiatorIds || [];
    if (this.selectedGladiatorIndex >= gladiatorIds.length) {
      canvas.drawText('Valitse gladiaattori', detailX + detailWidth / 2, detailY + detailHeight / 2,
        CONSTANTS.MAIN_FONT, '#666666', 'center');
      return;
    }

    const gladiator = this.gameState!.gladiators.get(gladiatorIds[this.selectedGladiatorIndex]);
    if (!gladiator) return;

    // Portrait
    const portrait = this.context.assets.gladiators[gladiator.race.id];
    if (portrait) {
      canvas.drawImage(portrait, detailX + 10, detailY + 10, 64, 64);
    }

    // Name and race
    canvas.drawText(gladiator.name, detailX + 85, detailY + 15, CONSTANTS.GLADIATOR_NAME_FONT, CONSTANTS.GLADIATOR_TITLE);
    canvas.drawText(gladiator.race.name, detailX + 85, detailY + 35, CONSTANTS.MAIN_FONT, '#000000');
    canvas.drawText(`Ikä: ${gladiator.age}`, detailX + 85, detailY + 52, CONSTANTS.MSG_FONT, '#666666');

    // Stats
    const statsY = detailY + 85;
    const col1X = detailX + 15;
    const col2X = detailX + 100;
    const col3X = detailX + 185;
    const lineHeight = 18;

    // Column 1: Base stats
    canvas.drawText('Voima:', col1X, statsY, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.str), col1X + 50, statsY, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Nopeus:', col1X, statsY + lineHeight, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(String(gladiator.speed), col1X + 50, statsY + lineHeight, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Kesto:', col1X, statsY + lineHeight * 2, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(`${gladiator.hp}/${gladiator.maxHp}`, col1X + 50, statsY + lineHeight * 2, CONSTANTS.MAIN_FONT, '#000066');

    canvas.drawText('Mana:', col1X, statsY + lineHeight * 3, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');
    canvas.drawText(`${gladiator.mana}/${gladiator.maxMana}`, col1X + 50, statsY + lineHeight * 3, CONSTANTS.MAIN_FONT, '#000066');

    // Column 2: Melee skills (attack/defense)
    canvas.drawText('Keihäs:', col2X, statsY, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[1]}/${gladiator.skills.meleeDef[1]}`, col2X + 45, statsY, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Miekka:', col2X, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[2]}/${gladiator.skills.meleeDef[2]}`, col2X + 45, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Kirves:', col2X, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[3]}/${gladiator.skills.meleeDef[3]}`, col2X + 45, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Nuija:', col2X, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(`${gladiator.skills.meleeAtk[4]}/${gladiator.skills.meleeDef[4]}`, col2X + 45, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000066');

    // Column 3: Defense/Ranged
    canvas.drawText('Jousi:', col3X, statsY, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.rangedAtk[0]), col3X + 45, statsY, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Varsi:', col3X, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.rangedAtk[1]), col3X + 45, statsY + lineHeight, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Väistö:', col3X, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.abilities[0]), col3X + 45, statsY + lineHeight * 2, CONSTANTS.MSG_FONT, '#000066');

    canvas.drawText('Vastust:', col3X, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000000');
    canvas.drawText(String(gladiator.skills.abilities[1]), col3X + 45, statsY + lineHeight * 3, CONSTANTS.MSG_FONT, '#000066');

    // Equipment section
    const equipY = statsY + lineHeight * 5;
    canvas.drawText('Varusteet:', col1X, equipY, CONSTANTS.GLADIATOR_STAT_TITLE_FONT, '#000000');

    const meleeWeapon = gladiator.items[0];
    const rangedWeapon = gladiator.items[1];
    const armor = gladiator.items[2];

    canvas.drawText(`Melee: ${meleeWeapon?.name ?? 'Ei asetta'}`, col1X, equipY + lineHeight, CONSTANTS.MSG_FONT, '#333333');
    canvas.drawText(`Ranged: ${rangedWeapon?.name ?? 'Ei asetta'}`, col1X, equipY + lineHeight * 2, CONSTANTS.MSG_FONT, '#333333');
    canvas.drawText(`Armor: ${armor?.name ?? 'Ei haarniskaa'}`, col1X, equipY + lineHeight * 3, CONSTANTS.MSG_FONT, '#333333');

    // Salary
    canvas.drawText(`Palkka: ${gladiator.salary} ${GAME_CONSTANTS.MONEY_SUFFIX}/kk`, col1X, equipY + lineHeight * 5, CONSTANTS.MAIN_FONT, '#660000');
  }

  private renderDivisionStandings(canvas: Canvas, team: any): void {
    const standingsX = 570;
    const standingsY = 95;
    const standingsWidth = 205;
    const standingsHeight = 330;

    // Background
    canvas.drawRect(standingsX, standingsY, standingsWidth, standingsHeight, '#ffffff');
    canvas.drawRectOutline(standingsX, standingsY, standingsWidth, standingsHeight, CONSTANTS.TABLE_BORDER);

    // Header
    const divisionNum = team.division + 1;
    canvas.drawText(`Divisioona ${divisionNum}`, standingsX + 10, standingsY + 5, CONSTANTS.MAIN_FONT, '#000000');

    // Teams in division
    const divisionTeams = getTeamsInDivision(this.gameState!, team.division);
    const itemHeight = 30;
    const startY = standingsY + 30;

    // Sort by wins
    divisionTeams.sort((a, b) => b.wins - a.wins);

    for (let i = 0; i < divisionTeams.length; i++) {
      const t = divisionTeams[i];
      const itemY = startY + i * itemHeight;

      // Highlight current team
      if (t.id === team.id) {
        canvas.drawRect(standingsX + 2, itemY, standingsWidth - 4, itemHeight - 2, '#ffffcc');
      }

      // Position
      canvas.drawText(`${i + 1}.`, standingsX + 10, itemY + 8, CONSTANTS.MAIN_FONT, '#000000');

      // Team name
      canvas.drawText(t.name, standingsX + 30, itemY + 8, CONSTANTS.MAIN_FONT, '#000000');

      // Wins
      canvas.drawText(`${t.wins} V`, standingsX + standingsWidth - 40, itemY + 8, CONSTANTS.MAIN_FONT, '#000066');
    }
  }

  private renderActionButtons(canvas: Canvas, _team: any): void {
    const buttonY = 440;
    const buttonHeight = 35;
    const buttonSpacing = 10;
    const buttonWidth = 100;
    const startX = 20;

    const buttons = [
      { id: 'recruit', text: 'Värväys', screen: ScreenState.RECRUIT },
      { id: 'shop', text: 'Kauppa', screen: ScreenState.SHOP },
      { id: 'mage', text: 'Velhot', screen: ScreenState.MAGE },
      { id: 'school', text: 'Koulu', screen: ScreenState.SCHOOL },
      { id: 'teams', text: 'Joukkueet', screen: ScreenState.OTHER_TEAMS },
    ];

    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      const btnX = startX + i * (buttonWidth + buttonSpacing);

      canvas.drawButton(
        btn.id,
        btn.text,
        btnX,
        buttonY,
        buttonWidth,
        buttonHeight,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        CONSTANTS.BUTTON_AREA_BACKGROUND,
        () => {
          console.log(`${btn.text} button clicked`);
          this.context.onScreenChange(btn.screen);
        }
      );
    }

    // Battle button - must participate in a battle before advancing month
    const battleX = CONSTANTS.CANVAS_WIDTH - 130;
    canvas.drawButton(
      'battle',
      'Taistelu',
      battleX,
      buttonY,
      110,
      buttonHeight,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#006600',
      () => {
        console.log('Battle clicked');
        this.context.onScreenChange(ScreenState.BATTLE_SETUP);
      }
    );

    // Watch AI Battle button (spectator mode)
    if (this.onStartSpectatorBattle) {
      canvas.drawButton(
        'watch-battle',
        'Katso AI',
        battleX,
        buttonY + buttonHeight + 5,
        110,
        buttonHeight - 5,
        CONSTANTS.MSG_FONT,
        '#ffffff',
        '#666600',
        () => {
          console.log('Watch AI Battle clicked');
          this.onStartSpectatorBattle!();
        }
      );
    }

    // Save game button - positioned after the regular buttons
    const saveX = startX + buttons.length * (buttonWidth + buttonSpacing);
    canvas.drawButton(
      'save-game',
      'Tallenna',
      saveX,
      buttonY,
      80,
      buttonHeight,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#444444',
      () => {
        if (this.context.onSaveGame) {
          this.context.onSaveGame();
        }
      }
    );

    // Settings button
    canvas.drawButton(
      'settings',
      'Asetukset',
      saveX + 90,
      buttonY,
      80,
      buttonHeight,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#444444',
      () => {
        this.context.onScreenChange(ScreenState.SETTINGS);
      }
    );

    // Bottom info bar
    canvas.drawRect(0, CONSTANTS.CANVAS_HEIGHT - 40, CONSTANTS.CANVAS_WIDTH, 40, CONSTANTS.BOTTOM_TXT_BACKGROUND);
    canvas.drawText(
      'Valitse toiminto ja käy taistelussa siirtyäksesi seuraavaan kuukauteen',
      CONSTANTS.CANVAS_WIDTH / 2,
      CONSTANTS.CANVAS_HEIGHT - 28,
      CONSTANTS.BOTTOM_TXT_FONT,
      '#ffffff',
      'center'
    );
  }

  handleClick(x: number, y: number): void {
    console.log(`MainMenu click at (${x}, ${y})`);
  }

  handleKeyDown(key: string): void {
    if (!this.gameState) return;

    const team = getCurrentTeam(this.gameState);
    if (!team) return;

    const gladiatorIds = team.gladiatorIds || [];

    if (key === 'ArrowUp') {
      this.selectedGladiatorIndex = Math.max(0, this.selectedGladiatorIndex - 1);
      if (gladiatorIds[this.selectedGladiatorIndex]) {
        this.gameState.currentGladiatorId = gladiatorIds[this.selectedGladiatorIndex];
      }
    } else if (key === 'ArrowDown') {
      this.selectedGladiatorIndex = Math.min(gladiatorIds.length - 1, this.selectedGladiatorIndex + 1);
      if (gladiatorIds[this.selectedGladiatorIndex]) {
        this.gameState.currentGladiatorId = gladiatorIds[this.selectedGladiatorIndex];
      }
    } else if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.TITLESCREEN);
    }
  }
}
